import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useUrlTab } from "../hooks/useUrlTab";
import { useCachedResource } from "../hooks/useCachedResource";
import { AcctShell } from "../features/acct/AcctShell";
import { DuesCards } from "../features/acct/DuesCards";
import { DuesList } from "../features/acct/DuesList";
import { RequestApprovalDialog } from "../features/acct/RequestApprovalDialog";
import { ShareLinkDialog } from "../features/acct/ShareLinkDialog";
import { approveDue, duesSummary, listDues } from "../api/supplierPayables";

const STATE_BY_TAB = {
  posted: "paid",
  future: null,
  upcoming: null,
  due: null,
  overdue: null,
  all: null,
};

export default function AcctSupplierDuesPage() {
  const { t } = useTranslation();
  const [tab, setTab] = useUrlTab("all", "tab");
  const [chosen, setChosen] = useState(null);
  const [askApproval, setAskApproval] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [shareLink, setShareLink] = useState("");

  const { data, reload: load } = useCachedResource(`acct-dues-${tab}`, async () => ({
    summary: await duesSummary(),
    items: await listDues(STATE_BY_TAB[tab] ? { state: STATE_BY_TAB[tab] } : {}),
  }), [tab]);
  const summary = data?.summary || {};
  const items = data?.items || [];

  const openRow = (row) => {
    setChosen(row);
    if (row.state === "pending") setAskApproval(true);
    if (row.state === "pending_confirmation" && row.payment_link_token) {
      const link = `${window.location.origin}/pc/${row.payment_link_token}`;
      setShareLink(link);
      setShareOpen(true);
    }
  };

  const doApprove = async () => {
    if (!chosen) return;
    const updated = await approveDue(chosen.id);
    toast.success(t("acct.dues.approved"));
    const link = `${window.location.origin}/pc/${updated.payment_link_token}`;
    setShareLink(link);
    setShareOpen(true);
    load();
  };

  return (
    <AcctShell titleKey="supplierDues">
      <DuesCards summary={summary} active={tab} onSelect={setTab} />
      <div className="mt-4 flex gap-2">
        {chosen?.state === "approval_pending" ? (
          <button className="rounded-full bg-primary px-4 py-1.5 text-sm font-bold text-primary-foreground"
                  onClick={doApprove} data-testid="approve-btn">
            {t("acct.dues.approve")}
          </button>
        ) : null}
      </div>
      <DuesList items={items} onOpen={openRow} />
      <RequestApprovalDialog payable={chosen} open={askApproval}
                             onOpenChange={setAskApproval} onDone={load} />
      <ShareLinkDialog open={shareOpen} onOpenChange={setShareOpen} link={shareLink}
                       title={t("acct.dues.share.title")} />
    </AcctShell>
  );
}
